import { Button, Card, Divider, Tooltip } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionsTableMenu } from 'src/components/atoms/actions-table-menu';
import { useDialog } from 'src/hooks/use-dialog';

import { PageContainer } from '../../../../components/molecules/page-container';
import { PageHeader } from '../../../../components/molecules/page-header';
import { Host } from '../../api/types/host';
import { useHostsWithSearch } from '../../hooks/query/use-hosts-with-search';
import CreateHostDialog from '../organisms/dialogs/create-host-dialog/create-host-dialog';
import { FilterHostsByBillingGroupDialog } from '../organisms/dialogs/filter-hosts-by-billing-group-dialog';
import { FilterHostsByDestinationGroupDialog } from '../organisms/dialogs/filter-hosts-by-destination-group-dialog';
import RemoveHostDialog from '../organisms/dialogs/remove-host-dialog';
import { UpdateHostBillingGroupDialog } from '../organisms/dialogs/update-host-billing-group-dialog';
import { UpdateHostDestinationGroupDialog } from '../organisms/dialogs/update-host-destination-group-dialog';
import { UpdateHostDestinationsDialog } from '../organisms/dialogs/update-host-destinations-dialog';
import HostsFilters from '../organisms/host-filters';
import { HostsTable } from '../organisms/hosts-table';

import { Pagination } from '@/components/organisms/pagination';
import { BillingGroup } from '@/features/billing-groups/api/types/billing-group';
import { DestinationGroup } from '@/features/destination-groups/api/types/destination-group';
import { usePagination } from '@/hooks/use-pagination';
import { Can, usePermissions, withPermissions } from '@/permissions/can';

const Hosts = () => {
  const { t } = useTranslation(['hosts', 'common']);

  const permissions = usePermissions();

  const [isOpenCreateHostDialog, handleOpenCreateHostDialog, handleCloseCreateHostDialog] =
    useDialog();

  const [isOpenRemoveHostDialog, handleOpenRemoveHostDialog, handleCloseRemoveHostDialog] =
    useDialog();

  const [query, setQuery] = useState('');

  const handleChangeQuery = (query: string) => {
    setQuery(query);
    changePage(1);
  };
  const [selectedSearchDestinationGroup, setSelectedSearchDestinationGroup] =
    useState<DestinationGroup | null>(null);

  const [selectedSearchBillingGroup, setSelectedSearchBillingGroup] = useState<BillingGroup | null>(
    null,
  );

  const [selectedHost, setSelectedHost] = useState<Host | null>(null);

  const { page, perPage, changePage, changePerPage, setTotalItems, totalPages } = usePagination();

  const { data, isLoading, isFetching, error } = useHostsWithSearch({
    page,
    perPage,
    email: query,
    billingGroupId: selectedSearchBillingGroup?.id.toString(),
    destinationGroupId: selectedSearchDestinationGroup?.id.toString(),
  });

  const [
    isOpenUpdateHostDestinationsDialog,
    handleOpenUpdateHostDestinationsDialog,
    handleCloseUpdateHostDestinationsDialog,
  ] = useDialog();

  //Billing groups
  const [
    isOpenUpdateBillingGroupDialog,
    handleOpenUpdateBillingGroupDialog,
    handleCloseUpdateBillingGroupDialog,
  ] = useDialog();

  //Destination groups
  const [
    isOpenUpdateDestinationGroupDialog,
    handleOpenUpdateDestinationGroupDialog,
    handleCloseUpdateDestinationGroupDialog,
  ] = useDialog();

  //filters by destination group
  const [
    isOpenFilterByDestinationGroup,
    handleOpenFilterByDestinationGroup,
    handleCloseFilterByDestinationGroup,
  ] = useDialog();

  const handleFilterByDestinationGroup = (destinationGroup: DestinationGroup) => {
    setSelectedSearchDestinationGroup(destinationGroup);
    handleCloseFilterByDestinationGroup();
  };

  //filters by billing group
  const [
    isOpenFilterByBillingGroup,
    handleOpenFilterByBillingGroup,
    handleCloseFilterByBillingGroup,
  ] = useDialog();

  const handleFilterByBillingGroup = async (billingGroup: BillingGroup) => {
    setSelectedSearchBillingGroup(billingGroup);
    handleCloseFilterByBillingGroup();
  };

  const actionsSlot = useCallback((host: Host) => {
    const actions = [
      {
        key: 'remove',
        label: t('common:button.delete'),
        onClick: () => {
          setSelectedHost(host);
          handleOpenRemoveHostDialog();
        },
        disabled: permissions.cannot('delete', 'Hosts'),
        tooltip: t('common:notSufficientPermissions'),
      },
    ];

    return <ActionsTableMenu actions={actions} />;
  }, []);

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data]);

  return (
    <>
      <PageContainer helmetPageTitle={t('hosts:tab')}>
        <PageHeader
          title={t('hosts:title')}
          extras={
            <Can I="create" a="Hosts" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      variant="contained"
                      size="large"
                      disabled={!allowed}
                      onClick={handleOpenCreateHostDialog}>
                      {t('hosts:inviteHost')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          }
        />
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}>
          <HostsFilters
            handleChangeQuery={handleChangeQuery}
            query={query}
            handleOpenFilterByDestinationGroup={handleOpenFilterByDestinationGroup}
            handleOpenFilterByBillingGroup={handleOpenFilterByBillingGroup}
            selectedSearchBillingGroup={selectedSearchBillingGroup}
            selectedSearchDestinationGroup={selectedSearchDestinationGroup}
            setSelectedSearchBillingGroup={setSelectedSearchBillingGroup}
            setSelectedSearchDestinationGroup={setSelectedSearchDestinationGroup}
          />
          <HostsTable
            data={data?.resources || []}
            error={error?.response?.data?.message || ''}
            isLoading={isLoading}
            actionsSlot={actionsSlot}
            setSelectedHost={setSelectedHost}
            handleOpenUpdateDestinationGroupDialog={handleOpenUpdateDestinationGroupDialog}
            handleOpenUpdateBillingGroupDialog={handleOpenUpdateBillingGroupDialog}
            handleOpenUpdateHostDestinationsDialog={handleOpenUpdateHostDestinationsDialog}
          />
          <Divider sx={{ mt: 'auto' }} />
          <Pagination
            isFetching={isFetching}
            changePage={changePage}
            page={page}
            changePerPage={changePerPage}
            totalPages={totalPages}
            perPage={perPage}
          />
        </Card>
      </PageContainer>
      <CreateHostDialog open={isOpenCreateHostDialog} onCancel={handleCloseCreateHostDialog} />
      <RemoveHostDialog
        open={isOpenRemoveHostDialog}
        onCancel={handleCloseRemoveHostDialog}
        selectedHostId={selectedHost?.id || 0}
        selectedHostEmail={selectedHost?.email ?? ''}
      />
      <UpdateHostDestinationsDialog
        open={isOpenUpdateHostDestinationsDialog}
        hostId={selectedHost?.id || 0}
        destinationsIds={selectedHost?.destinations?.map(dest => dest.id) || []}
        onCancel={handleCloseUpdateHostDestinationsDialog}
      />
      <UpdateHostBillingGroupDialog
        open={isOpenUpdateBillingGroupDialog}
        onCancel={handleCloseUpdateBillingGroupDialog}
        billingGroupId={selectedHost?.billingGroup?.id || 0}
        hostId={selectedHost?.id || 0}
      />
      <UpdateHostDestinationGroupDialog
        open={isOpenUpdateDestinationGroupDialog}
        onCancel={handleCloseUpdateDestinationGroupDialog}
        destinationGroupId={selectedHost?.destinationGroup?.id || 0}
        hostId={selectedHost?.id || 0}
      />
      <FilterHostsByBillingGroupDialog
        open={isOpenFilterByBillingGroup}
        onCancel={handleCloseFilterByBillingGroup}
        selectedSearchGroup={selectedSearchDestinationGroup}
        handleFilterByBillingGroup={handleFilterByBillingGroup}
      />
      <FilterHostsByDestinationGroupDialog
        open={isOpenFilterByDestinationGroup}
        onCancel={handleCloseFilterByDestinationGroup}
        selectedSearchGroup={selectedSearchDestinationGroup}
        handleFilterByDestinationGroup={handleFilterByDestinationGroup}
      />
    </>
  );
};

export default withPermissions(permissions => permissions.cannot('read', 'Hosts'), Hosts);
